import { Controller } from "stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static targets = ["formulaDisplay", "comparisonDisplay", "comparisonSelector", "comparisonSelect"];

  connect() {
    this.blockId = this.element.dataset.blockId;
    this.blockType = 'formula';
    this.isReadOnly = this.data.get("readOnly") === "true";
    this.decimalPlaces = parseInt(this.data.get("decimalPlaces") || "0");
    this.symbol = this.data.get("symbol") || "";
    this.symbolPosition = this.data.get("symbolPosition") || this.defaultPositionFor(this.symbol);
    this.compareEnabled = this.data.get("compareEnabled") === "true";
    this.comparisonProjectId = this.data.get("comparisonProjectId");

    this.boundHandleComparisonProjectUpdate = this.handleComparisonProjectUpdate.bind(this);
    this.boundHandleComparisonDisplayUpdate = this.handleComparisonDisplayUpdate.bind(this);
    this.boundHandleComparisonDisplayRemove = this.handleComparisonDisplayRemove.bind(this);
    
    if (this.hasComparisonSelectTarget) {
      this.kanbanSubscription = consumer.subscriptions.create("KanbanChannel", {
        received: this.handleKanbanBroadcast.bind(this)
      });
      
      this.comparisonSelectTarget.addEventListener('comparisonProjectUpdated', this.boundHandleComparisonProjectUpdate);
    }
    
    this.element.addEventListener('comparisonDisplayUpdate', this.boundHandleComparisonDisplayUpdate);
    this.element.addEventListener('comparisonDisplayRemove', this.boundHandleComparisonDisplayRemove);
  }

  updateComparison(event) {
    const projectId = event.target.value;
    const { accountId, projectId: currentProjectId, taskListId, taskId, blockId } = this.element.dataset;

    // Update via the standard update endpoint
    const formData = new FormData();
    formData.append('task_block[comparison_project_id]', projectId || '');
    formData.append('authenticity_token', document.querySelector('meta[name="csrf-token"]').content);

    fetch(`/${accountId}/projects/${currentProjectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData
    })
    .then(response => {
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return response.text().then(text => {
          throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
        });
      }
      
      
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || data.message || `Server error: ${response.status}`);
        });
      }
      
      return response.json();
    })
    .then(data => {
      if (data.success) {
        this.comparisonProjectId = projectId || null;
        
        // Update only the comparison display, not the entire page
        if (data.comparison_data) {
          this.updateComparisonDisplay(data.comparison_data);
        } else {
          // No comparison data available - remove existing comparison display
          this.removeComparisonDisplay();
        }
      } else {
        console.error("Failed to update comparison project:", data);
      }
    })
    .catch(error => {
      console.error("Error updating comparison project:", error);
    });
  }

  updateComparisonDisplay(comparisonData) {
    this.removeComparisonDisplay();
    
    const numberInputWrapper = this.element.querySelector('.number-input-wrapper');
    if (!numberInputWrapper) return;
    
    
    let displayRow = numberInputWrapper.querySelector('.number-display-row');
    if (!displayRow) {
    
      displayRow = document.createElement('div');
      displayRow.className = 'number-display-row';
      const formulaDisplay = numberInputWrapper.querySelector('[data-target="formula-input.formulaDisplay"]');
      if (formulaDisplay) {
        numberInputWrapper.insertBefore(displayRow, formulaDisplay);
        displayRow.appendChild(formulaDisplay);
      }
    }
    
   
    const comparisonWrapper = document.createElement('div');
      comparisonWrapper.className = 'number-comparison-wrapper';
      displayRow.appendChild(comparisonWrapper);
    
    const comparisonDisplay = document.createElement('span');
      comparisonDisplay.setAttribute('data-target', 'formula-input.comparisonDisplay');
    comparisonDisplay.className = `number-comparison ${comparisonData.color_class}`;
    comparisonDisplay.innerHTML = `
      <span class="icon comparison-icon ${comparisonData.icon}"></span>
      <span>${comparisonData.percentage}% (from ${comparisonData.formatted_previous_value})</span>
    `;
    comparisonWrapper.appendChild(comparisonDisplay);
    comparisonWrapper.classList.remove('hidden');
  }

  removeComparisonDisplay() {
    // Remove all comparison wrappers to prevent duplicates
    const numberInputWrapper = this.element.querySelector('.number-input-wrapper');
    if (numberInputWrapper) {
      
      const comparisonWrappers = numberInputWrapper.querySelectorAll('.number-comparison-wrapper');
      comparisonWrappers.forEach(wrapper => {
        wrapper.remove();
      });
      
      
      const displayRow = numberInputWrapper.querySelector('.number-display-row');
      if (displayRow) {
        const rowComparisonWrappers = displayRow.querySelectorAll('.number-comparison-wrapper');
        rowComparisonWrappers.forEach(wrapper => {
          wrapper.remove();
        });
      }
    }
  }

  defaultPositionFor(symbol) {
    const frontSymbols = ["$", "£", "€", "¥"];
    return frontSymbols.includes(symbol) ? "front" : "back";
  }

  handleKanbanBroadcast(data) {
    const { task_data } = data;
    
    if (task_data && task_data.update_type === 'update_project' && task_data.project_id && task_data.updated_title) {
      
      if (this.hasComparisonSelectTarget) {
        const selectElement = this.comparisonSelectTarget;
        const option = selectElement.querySelector(`option[value="${task_data.project_id}"]`);
        if (option) {
          option.textContent = task_data.updated_title;
        }
      }
    }
  }

  handleComparisonProjectUpdate(event) {
    const { comparison_project_id, comparison_data } = event.detail;
    this.comparisonProjectId = comparison_project_id || null;
    
    
    if (comparison_data) {
      this.updateComparisonDisplay(comparison_data);
    } else {
      this.removeComparisonDisplay();
    }
  }

  handleComparisonDisplayUpdate(event) {
    const { comparison_data } = event.detail;
    if (comparison_data) {
      this.updateComparisonDisplay(comparison_data);
    }
  }

  handleComparisonDisplayRemove(event) {
    this.removeComparisonDisplay();
  }

  disconnect() {
    if (this.kanbanSubscription) {
      consumer.subscriptions.remove(this.kanbanSubscription);
    }
    
    if (this.hasComparisonSelectTarget && this.boundHandleComparisonProjectUpdate) {
      this.comparisonSelectTarget.removeEventListener('comparisonProjectUpdated', this.boundHandleComparisonProjectUpdate);
    }
    
    if (this.boundHandleComparisonDisplayUpdate) {
      this.element.removeEventListener('comparisonDisplayUpdate', this.boundHandleComparisonDisplayUpdate);
    }
    
    if (this.boundHandleComparisonDisplayRemove) {
      this.element.removeEventListener('comparisonDisplayRemove', this.boundHandleComparisonDisplayRemove);
    }
  }
}
